import {
  CampaignActionRequest,
  CampaignActionRequestCampaignAction,
  CampaignDetailModelStage,
  DocumentTypes,
  usePerformCampaignAction,
  useInvalidateUseStaffGetCampaign,
} from '@urbanx/agx.campaigns.client';
import {
  ActivitiesApiContext,
  TaskCategoryTaskType,
  useInvalidateUseGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import { AgxFileUpload } from '@urbanx/agx-ui-components';
import {
  AgxAnimatedToggleLoaderOverlay,
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxYesNoQuestion,
  Icon_AlertCircleOutline,
  Icon_FeaturedIcon,
  useToast,
  YesNo,
} from '@urbanx/agx.ui-components2';
import { useSelectedCampaign } from 'hooks';
import { waitForTaskCollectionRefresh } from 'hooks/taskCollectionRefresh';
import { useContext, useEffect, useRef, useState } from 'react';
import { deleteFile, getFileLink, uploadFile } from 'services';

const WithdrawListingAction = () => {
  const { campaign, isLoading } = useSelectedCampaign();
  const invalidateCampaign = useInvalidateUseStaffGetCampaign();
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();
  const activitiesApi = useContext(ActivitiesApiContext);
  const { getAuthToken } = useContext(AuthContext);
  const { pushToast } = useToast();
  const [hasFundInTrust, setHasFundInTrust] = useState<boolean | undefined>();
  const [withdrawalConfirmationFiles, setWithdrawalConfirmationFiles] = useState<File[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawalSucceeded, setWithdrawalSucceeded] = useState(false);
  const withdrawalStatusRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (withdrawalSucceeded && !showConfirmation) withdrawalStatusRef.current?.focus();
  }, [withdrawalSucceeded, showConfirmation, campaign?.stage]);
  const isWithdrawalDisabled =
    isLoading ||
    !campaign?.campaignId ||
    isWithdrawing ||
    campaign.stage === CampaignDetailModelStage.ListingWithdrawn;

  const { performCampaignAction } = usePerformCampaignAction({
    onSuccess: () => {},
    onError: () => {},
  });

  const confirmWithdrawal = async () => {
    const campaignId = campaign?.campaignId;
    const withdrawalConfirmationFile = withdrawalConfirmationFiles[0];
    if (
      isWithdrawalDisabled ||
      hasFundInTrust === undefined ||
      !campaignId ||
      !withdrawalConfirmationFile
    )
      return;

    const request: CampaignActionRequest = {
      campaignId,
      campaignAction: CampaignActionRequestCampaignAction.Withdraw,
      hasFundInTrust,
    };

    setIsWithdrawing(true);
    let cancelTaskCollectionRefresh: (() => void) | undefined;

    try {
      const authToken = await getAuthToken();
      if (!authToken) return;

      const formData = new FormData();
      formData.append('File', withdrawalConfirmationFile);
      const uploadedDocument = await uploadFile(
        authToken,
        formData,
        campaignId,
        DocumentTypes.WithdrawalConfirmation
      );
      if (!uploadedDocument) {
        pushToast({
          type: 'error',
          message:
            'The withdrawal confirmation could not be uploaded. Check the file and try again.',
        });
        return;
      }

      const taskCollectionRefresh = waitForTaskCollectionRefresh(campaignId);
      cancelTaskCollectionRefresh = taskCollectionRefresh.cancel;
      await performCampaignAction(request);
      await invalidateCampaign(campaignId);

      let withdrawalTaskExists = false;
      try {
        const tasks = await activitiesApi.getAutopilotTasksForCampaign(campaignId);
        withdrawalTaskExists = tasks.some(
          (task) => task.category?.taskType === TaskCategoryTaskType.Withdrawn
        );
      } catch {
        // The notification can still confirm the workflow when the task read fails.
      }

      if (withdrawalTaskExists) {
        await invalidateTasks(campaignId);
      } else if ((await taskCollectionRefresh.wait()) === 'timed-out') {
        pushToast({
          type: 'error',
          message:
            'The listing was withdrawn, but the task refresh could not be confirmed. Refresh the page to check its latest status.',
        });
      }

      setWithdrawalSucceeded(true);
      setShowConfirmation(false);
    } finally {
      cancelTaskCollectionRefresh?.();
      setIsWithdrawing(false);
    }
  };

  return (
    <>
      <AgxButton
        id="btnWithdrawListing"
        text="Withdraw Listing"
        variant="naked"
        danger
        onClick={() => {
          setWithdrawalSucceeded(false);
          setShowConfirmation(true);
        }}
        disabled={isWithdrawalDisabled}
      />
      {withdrawalSucceeded && campaign?.stage === CampaignDetailModelStage.ListingWithdrawn && (
        <p ref={withdrawalStatusRef} role="status" tabIndex={-1}>
          Listing withdrawn
        </p>
      )}
      <AgxModal
        title="Mark as Withdrawn?"
        titleIcon={
          <>
            {isWithdrawing && (
              <AgxAnimatedToggleLoaderOverlay
                background="transparent"
                label="Withdrawing listing..."
              />
            )}
            <Icon_FeaturedIcon
              icon={<Icon_AlertCircleOutline type="Danger" />}
              color="var(--text-error)"
              backgroundColor="var(--bg-error-light)"
            />
          </>
        }
        type="single"
        variant="withDivider"
        actionButtonContainerStyle="withoutDivider"
        actionButtons={
          <>
            <AgxButton
              id="btnCancel"
              text="Cancel"
              variant="hollow"
              size="large"
              onClick={() => setShowConfirmation(false)}
              stretch
            />
            <AgxButton
              id="btnConfirm"
              text="Confirm"
              variant="primary"
              danger
              size="large"
              onClick={() => void confirmWithdrawal()}
              stretch
              disabled={
                hasFundInTrust === undefined ||
                withdrawalConfirmationFiles.length === 0 ||
                isWithdrawalDisabled
              }
            />
          </>
        }
        content={
          <AgxSpacer
            direction="vertical"
            gap="md"
            fill
            padding={{ top: 'md', bottom: 'xl', left: 'xl', right: 'xl' }}
          >
            <AgxFileUpload
              title="Withdrawal Confirmation"
              id="fileWithdrawalConfirmation"
              documentType={DocumentTypes.WithdrawalConfirmation}
              buttonText="Choose file"
              fileData={withdrawalConfirmationFiles}
              onValueChanged={({ value }: { id: string; value: File[] }) =>
                setWithdrawalConfirmationFiles(value)
              }
              getAuthToken={getAuthToken}
              campaignId={campaign?.campaignId ?? ''}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              getFileLink={getFileLink}
              extensions={['pdf']}
              multiple={false}
              autoUpload={false}
              required
            />
            <AgxYesNoQuestion
              id="fundsInTrustQuestion"
              onValueChanged={({ value }: { id: string; value: string }) =>
                setHasFundInTrust(value === YesNo.Yes)
              }
              defaultValue={
                hasFundInTrust === undefined ? undefined : hasFundInTrust ? YesNo.Yes : YesNo.No
              }
              label="Is there surplus marketing funds?"
              yesLabel="Yes"
              noLabel="No"
              required
            />
          </AgxSpacer>
        }
        isOpen={showConfirmation}
        setIsDialogOpen={setShowConfirmation}
      />
    </>
  );
};

export default WithdrawListingAction;
